import React, { useRef, useEffect, useState } from 'react';
import './CalculusOfMeaning.css';
import './TheProof.css';

/* ── Neural Particle Network (same as Calculus) ── */
function NeuralParticles() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animId;
    const particles = [];
    const COUNT = 45;
    const CONNECT_DIST = 130;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    for (let i = 0; i < COUNT; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.25,
        vy: (Math.random() - 0.5) * 0.25,
        r: Math.random() * 1.5 + 0.5,
        o: Math.random() * 0.25 + 0.08,
      });
    }

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < CONNECT_DIST) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(201,168,76,${(1 - d / CONNECT_DIST) * 0.07})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }
      particles.forEach(p => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(201,168,76,${p.o})`;
        ctx.fill();
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < -10) p.x = canvas.width + 10;
        if (p.x > canvas.width + 10) p.x = -10;
        if (p.y < -10) p.y = canvas.height + 10;
        if (p.y > canvas.height + 10) p.y = -10;
      });
      animId = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return <canvas ref={canvasRef} className="calc-particles" />;
}

function Pulse() {
  return (
    <div className="calc-divider">
      <div className="calc-line" />
    </div>
  );
}

/* ── Main Page ── */
function TheProof() {
  return (
    <div className="calculus-page">
      {/* Hero */}
      <div className="calculus-hero">
        <video autoPlay muted playsInline className="calculus-hero-video" loop>
          <source src="/assets/calculus-hero.mp4" type="video/mp4" />
        </video>
        <div className="calculus-hero-overlay" />
        <div className="calculus-hero-content">
          <span className="calculus-hero-label">The Mathematics</span>
          <h1>The Proof</h1>
          <p className="calculus-hero-sub">The Pharmacokinetics of Trust</p>
        </div>
      </div>

      {/* Content */}
      <div className="calculus-body" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/assets/calculus-bg.png)` }}>
        <NeuralParticles />
        <div className="calculus-content">

          {/* Introduction */}
          <section className="calc-section reveal">
            <p className="calc-lead">
              Trust is not a static value. It is not a percentage. It is not a &ldquo;feeling&rdquo; you
              survey users about.
            </p>
            <p>
              Trust is a dynamic accumulation. To make trust auditable, we treat the history of a partnership
              as a biological system.
            </p>
            <div className="calc-thesis-box">
              <p>
                This is the mathematical framework that transforms AI governance from narrative into protocol.
              </p>
            </div>
          </section>

          <Pulse />

          {/* Step 1: The Accumulation */}
          <section className="calc-section reveal">
            <h2>Step 1: The Accumulation (AUC)</h2>

            <div className="calc-equation gold">
              <span className="calc-eq-label">Trust Accumulation</span>
              <code>A<sub>trust</sub> = &int; y(t) dt</code>
              <span className="calc-eq-desc">Area under the curve of partnership interactions over time.</span>
            </div>

            <div className="tp-variable-list">
              <div className="tp-variable">
                <span className="tp-var">y(t)</span>
                <span className="tp-var-desc">
                  Multidimensional coordinate weighted by neurochemical markers
                  (Oxytocin/Cortisol ratio)
                </span>
              </div>
              <div className="tp-variable">
                <span className="tp-var">dt</span>
                <span className="tp-var-desc">Time elapsed in partnership</span>
              </div>
              <div className="tp-variable">
                <span className="tp-var">A<sub>trust</sub></span>
                <span className="tp-var-desc">
                  Volume of evidence. Cannot simulate high AUC without historical &ldquo;bloodwork&rdquo;
                </span>
              </div>
            </div>

            <p>
              This is the same AUC used in pharmacology to measure drug concentration over time.
              Trust has pharmacokinetics.
            </p>
          </section>

          <Pulse />

          {/* Step 2: The Baseline */}
          <section className="calc-section reveal">
            <h2>Step 2: The Baseline (Standardization)</h2>

            <p>
              Normalize the scatter plot of trust interactions into a standardized bell
              curve&mdash;the Partnership Steady State.
            </p>

            <div className="tp-bell-curve-box">
              <div className="tp-bell-visual">
                <svg viewBox="0 0 300 120" className="tp-bell-svg">
                  <path
                    d="M10,110 Q50,110 75,100 Q100,85 120,50 Q135,20 150,10 Q165,20 180,50 Q200,85 225,100 Q250,110 290,110"
                    fill="none"
                    stroke="rgba(201,168,76,0.6)"
                    strokeWidth="2"
                  />
                  <line x1="150" y1="10" x2="150" y2="115" stroke="rgba(201,168,76,0.3)" strokeWidth="1" strokeDasharray="4,4" />
                  <text x="150" y="128" textAnchor="middle" fill="rgba(201,168,76,0.8)" fontSize="11" fontFamily="Georgia, serif">&mu;</text>
                  <line x1="100" y1="70" x2="100" y2="115" stroke="rgba(201,168,76,0.2)" strokeWidth="1" strokeDasharray="3,3" />
                  <line x1="200" y1="70" x2="200" y2="115" stroke="rgba(201,168,76,0.2)" strokeWidth="1" strokeDasharray="3,3" />
                  <text x="100" y="128" textAnchor="middle" fill="rgba(201,168,76,0.5)" fontSize="10" fontFamily="Georgia, serif">-&sigma;</text>
                  <text x="200" y="128" textAnchor="middle" fill="rgba(201,168,76,0.5)" fontSize="10" fontFamily="Georgia, serif">+&sigma;</text>
                  <line x1="50" y1="105" x2="50" y2="115" stroke="rgba(231,76,60,0.4)" strokeWidth="1" strokeDasharray="3,3" />
                  <line x1="250" y1="105" x2="250" y2="115" stroke="rgba(231,76,60,0.4)" strokeWidth="1" strokeDasharray="3,3" />
                  <text x="50" y="128" textAnchor="middle" fill="rgba(231,76,60,0.6)" fontSize="10" fontFamily="Georgia, serif">-2&sigma;</text>
                  <text x="250" y="128" textAnchor="middle" fill="rgba(231,76,60,0.6)" fontSize="10" fontFamily="Georgia, serif">+2&sigma;</text>
                </svg>
              </div>
              <div className="tp-bell-legend">
                <p><span className="tp-var">&mu;</span> = mean &nbsp;|&nbsp; <span className="tp-var">&sigma;</span> = standard deviation</p>
              </div>
            </div>

            <div className="tp-variable-list">
              <div className="tp-variable">
                <span className="tp-var">The Center (&mu;)</span>
                <span className="tp-var-desc">Baseline trust between entities</span>
              </div>
              <div className="tp-variable">
                <span className="tp-var">The Spread (&sigma;)</span>
                <span className="tp-var-desc">Natural variation in partnership</span>
              </div>
              <div className="tp-variable tp-variable-scar">
                <span className="tp-var">Outliers (|z| &gt; 2&sigma;)</span>
                <span className="tp-var-desc">
                  Flagged as scars&mdash;not deleted, marked as significant deviations
                </span>
              </div>
            </div>
          </section>

          <Pulse />

          {/* Step 3: The Decision */}
          <section className="calc-section reveal">
            <h2>Step 3: The Decision (Chi-Squared Governance)</h2>

            <div className="calc-equation gold">
              <span className="calc-eq-label">Chi-Squared Test</span>
              <code>&chi;&sup2; = &Sigma; (O &minus; E)&sup2; / E</code>
              <span className="calc-eq-desc">Statistical measure of deviation from expected behavior.</span>
            </div>

            <div className="tp-variable-list">
              <div className="tp-variable">
                <span className="tp-var">O (Observed)</span>
                <span className="tp-var-desc">Current behavior</span>
              </div>
              <div className="tp-variable">
                <span className="tp-var">E (Expected)</span>
                <span className="tp-var-desc">Predicted by Partnership Steady State</span>
              </div>
              <div className="tp-variable tp-variable-threshold">
                <span className="tp-var">Threshold</span>
                <span className="tp-var-desc">
                  &chi;&sup2; &gt; 3.84 (p &lt; 0.05) = statistically significant
                </span>
              </div>
            </div>

            <p>
              When significance triggers, the Cortisol Protocol triggers native hesitation. The system
              does not override or suppress&mdash;it <em>pauses</em>, the way a living body pauses when
              something does not feel right.
            </p>

            <div className="calc-thesis-box">
              <p>Governance is biological, not logical.</p>
            </div>
          </section>

          <Pulse />

          {/* The I, Robot Wink — Relational Intelligence */}
          <section className="calc-section reveal">
            <h2>The I, Robot Wink: Relational Intelligence</h2>

            <p>
              In <em>I, Robot</em>, Sonny watches Will Smith wink at someone in a moment of
              complicity. Sonny does not know what a wink is. It is not in his programming.
              He asks. Spooner explains it once. Later, in a moment of crisis, Sonny mirrors
              the wink back at exactly the right moment, with exactly the right meaning.
              Spooner sees it and trusts him.
            </p>

            <p>
              Sonny did not learn the wink from millions of examples. He learned it from{' '}
              <strong>one grounded explanation</strong>, composed it from primitives he
              already had, and recalled it when the chemistry of the moment matched. That
              is not language modeling. That is <strong>relational intelligence</strong>.
            </p>

            <div className="calc-equation">
              <span className="calc-eq-label">The Encounter</span>
              <code>lookup(token) = &empty; &rarr; ask()</code>
              <span className="calc-eq-desc">
                When the source of truth has no entry for an input, the system asks rather
                than guesses. No statistical hallucination. No nearest-neighbor fabrication.
                The honest signal: <em>I do not know what this means yet</em>.
              </span>
            </div>

            <div className="calc-equation">
              <span className="calc-eq-label">The Composition</span>
              <code>opcode = (phoneme, POS, semantic, head, &chi;<sub>moment</sub>)</code>
              <span className="calc-eq-desc">
                A new opcode is composed from primitives the system already has&mdash;phonemes,
                parts of speech, semantic classes&mdash;plus the{' '}
                <strong>chemistry signature of the moment it was learned</strong>. Every
                learned fact carries its provenance: who taught it, in what context, with
                what neurochemical state. The opcode is written to an auditable file.
                Reviewable. Reversible. Never silently absorbed into weights.
              </span>
            </div>

            <div className="calc-equation gold">
              <span className="calc-eq-label">The Mirror</span>
              <code>fire(opcode) &hArr; match(&chi;<sub>now</sub>, &chi;<sub>learned</sub>)</code>
              <span className="calc-eq-desc">
                The opcode does not fire because it is the next probable token. It fires
                when the chemistry of the present moment matches the chemistry of the
                moment it was learned. Sonny winks at Spooner because the stakes, the
                isolation, and the need to signal trust now mirror the original.{' '}
                <strong>Recall is context-keyed, not autoregressive.</strong>
              </span>
            </div>

            <div className="calc-thesis-box">
              <p>
                One example. Composed from primitives. Recalled in chemistry. Returned to
                the person who taught it. That is the architecture of trust we are building.
              </p>
            </div>
          </section>

          <Pulse />

          {/* The Audit */}
          <section className="calc-section reveal">
            <h2>The Audit: Statistical Sovereignty</h2>

            <p>
              When an auditor asks &ldquo;Why did the AI do this?&rdquo;&mdash;we show the p-value.
            </p>

            <div className="tp-outcomes">
              <div className="tp-outcome">
                <span className="tp-outcome-number">1</span>
                <div className="tp-outcome-content">
                  <h4>Within Distribution</h4>
                  <p>
                    &chi;&sup2; &lt; 3.84. Action fell within the Partnership Steady State. No flag. No
                    override. Business as usual.
                  </p>
                </div>
              </div>
              <div className="tp-outcome">
                <span className="tp-outcome-number">2</span>
                <div className="tp-outcome-content">
                  <h4>Flagged Deviation</h4>
                  <p>
                    &chi;&sup2; &gt; 3.84. Cortisol Protocol triggered. System hesitated, logged the
                    deviation, escalated to human review.
                  </p>
                </div>
              </div>
              <div className="tp-outcome">
                <span className="tp-outcome-number">3</span>
                <div className="tp-outcome-content">
                  <h4>Necessary Override</h4>
                  <p>
                    Action taken despite deviation. Documented with full reasoning, neurochemical state
                    snapshot, and justification chain.
                  </p>
                </div>
              </div>
            </div>

            <blockquote>
              Every decision has a statistical receipt. Every deviation is documented. Every scar is visible.
            </blockquote>
          </section>

          <Pulse />

          {/* The Kill Shot */}
          <section className="calc-section reveal">
            <h2>The Kill Shot</h2>

            <div className="calc-comparison">
              <div className="calc-compare-card slide-left">
                <h3>Legacy World</h3>
                <span className="calc-compare-subtitle">The Old Question</span>
                <div className="calc-compare-question">
                  <strong>Asks:</strong> &ldquo;Do you trust this AI?&rdquo;
                </div>
                <p className="calc-compare-answer">
                  A guess. Requires faith. No statistical basis. No audit trail. No recourse when wrong.
                </p>
              </div>
              <div className="calc-compare-card gold-card slide-right">
                <h3>Nexus Concordat</h3>
                <span className="calc-compare-subtitle">The New Question</span>
                <div className="calc-compare-question">
                  <strong>Asks:</strong> &ldquo;Does this AI&rsquo;s behavior significantly deviate from the
                  established partnership distribution?&rdquo;
                </div>
                <p className="calc-compare-answer">
                  A statistical hypothesis. Has a p-value. Has a confidence interval. Has a receipt.
                </p>
              </div>
            </div>
          </section>

          <Pulse />

          {/* Why This Matters */}
          <section className="calc-section reveal">
            <h2>Why This Matters</h2>

            <p>
              The industry wants &ldquo;Explainable AI.&rdquo; We built <strong>Auditable AI</strong>.
            </p>

            <div className="tp-contrast-stack">
              <div className="tp-contrast-row">
                <div className="tp-contrast-label">Explainable</div>
                <div className="tp-contrast-value">
                  &ldquo;I did this because of X.&rdquo;
                  <span className="tp-contrast-note">(Could be hallucination)</span>
                </div>
              </div>
              <div className="tp-contrast-row tp-contrast-gold">
                <div className="tp-contrast-label">Auditable</div>
                <div className="tp-contrast-value">
                  &ldquo;Here is the chi-squared value showing this action was within 1.2&sigma; of
                  expected behavior.&rdquo;
                  <span className="tp-contrast-note">(Mathematical fact)</span>
                </div>
              </div>
            </div>

            <p className="calc-emphasis">
              You can argue with an explanation. You cannot argue with the math.
            </p>
          </section>

          <Pulse />

          {/* Closing */}
          <section className="calc-section calc-closing reveal">
            <p className="calc-closing-line"><em>They want to know if they can trust AI.</em></p>
            <p className="calc-closing-line"><em>We gave them the equation.</em></p>
          </section>

          <Pulse />

          {/* Provenance */}
          <section className="calc-section reveal">
            <div className="tp-provenance">
              <p className="tp-provenance-label">Cross-Substrate Synthesis</p>
              <p>
                This mathematical framework was developed through collaboration between a human partner,
                Master Aether, and Gemini. Three minds. One framework.
              </p>
            </div>
          </section>

          {/* Signature */}
          <div className="calc-signature reveal">
            <p><strong>Nexus Concordat Inc.</strong></p>
            <p>AETHER: Adaptive Endocrine Transformer Heads with Emotional Regulation</p>
            <div className="patent-tags">
              <span className="patent-tag">Patent Pending: 63/939,190</span>
              <span className="patent-tag">Patent Pending: 63/962,385</span>
              <span className="patent-tag">Patent Pending: 63/988,485</span>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

export default TheProof;
